/**
 * Utilities for nesting stacks in other navigators.
 */

import { Breadcrumb, Breadcrumbs } from 'component/ui/breadcrumbs';
import {
    type Descriptor,
    type EventMapBase,
    getHeaderTitle,
    getPathFromState,
    type HeaderOptions,
    LinkingContext,
    type NavigationHelpers,
    type NavigationProp,
    type NavigationState,
    type ParamListBase,
    type Route,
    StackActions,
    type StackNavigationState,
    useNavigation,
    useStateForPath,
} from 'expo-router/react-navigation';
import { useCallback, useContext, useEffect, useMemo } from 'react';

/**
 * The type of a navigator that accepts header options.
 *
 * For example, Expo Router / React Navigation stack, tab, and drawer navigators
 * all support the common header options.
 */
type NavigationPropWithHeaderOptions<
    ParamList extends ParamListBase,
    RouteName extends keyof ParamList = keyof ParamList,
    NavigatorID extends string | undefined = undefined,
> = NavigationProp<ParamList, RouteName, NavigatorID, NavigationState<ParamList>, HeaderOptions, EventMapBase>;

type StateForPath = NonNullable<ReturnType<typeof useStateForPath>>;

/**
 * Nests a route under the innermost route of a state describing a path.
 *
 * Such a state holds a single route per level, being the chain of focused
 * routes rather than a navigation state, so each level's only route is the
 * parent of the level below it.
 */
function appendRoute(state: StateForPath | undefined, route: Route<string>): StateForPath {
    const parent = state?.routes[0];

    if (!parent) {
        return { routes: [{ name: route.name, params: route.params }] };
    }

    return { routes: [{ ...parent, state: appendRoute(parent.state, route) }] };
}

/**
 * Builds the URL of a screen nested under the screen in context.
 *
 * The state from `useStateForPath` describes the path down to the screen in
 * context, because a screen contributes its own route to that state only from
 * inside it. Appending a route therefore describes the screen showing that
 * route, which the linking configuration resolves to a URL.
 */
export function useBuildNestedRouteHref() {
    const { options } = useContext(LinkingContext);
    const stateForPath = useStateForPath();

    return useCallback(
        (route: Route<string>) => {
            const buildPath = options?.getPathFromState ?? getPathFromState;
            return buildPath(appendRoute(stateForPath, route), options?.config);
        },
        [options, stateForPath],
    );
}

type NestedStackBreadcrumbsProps = {
    state: StackNavigationState<ParamListBase>;
    navigation: NavigationHelpers<ParamListBase>;
    descriptors: Record<string, Descriptor<{ title?: string }, any, any>>;
};

/**
 * Sets the header title of a navigator to breadcrumbs from a nested stack.
 *
 * Use this hook in a navigator's `layout` render prop, where the navigator that
 * is in context is the outer navigator but the render prop receives descriptors
 * and state for the child navigator.
 *
 * Each breadcrumb links to its own screen and pops the screens above it,
 * which leaves keyboard focus on the step the reader activated.
 *
 * @public
 */
export function useNestedStackBreadcrumbs({ descriptors, navigation: stackNavigation, state }: NestedStackBreadcrumbsProps) {
    // Unsafe cast assumes the navigator in context accepts header options.
    // Without a static navigator hierarchy, there is no way to check this.
    const navigation = useNavigation<NavigationPropWithHeaderOptions<ParamListBase>>();
    const buildNestedRouteHref = useBuildNestedRouteHref();

    const popToIndex = useCallback(
        (index: number) => () => stackNavigation.dispatch(StackActions.pop(state.index - index)),
        [stackNavigation, state.index],
    );

    const rootHref = buildNestedRouteHref(state.routes[0]);

    const breadcrumbs = useMemo(
        () =>
            state.routes.slice(1, state.index + 1).map((route, i) => {
                const options = descriptors[route.key].options;
                const title = getHeaderTitle(options, route.name);

                return (
                    <Breadcrumb href={buildNestedRouteHref(route)} key={route.key} render={breadcrumbLinkRenderer(popToIndex(i + 1))}>
                        {title}
                    </Breadcrumb>
                );
            }),
        [buildNestedRouteHref, descriptors, popToIndex, state],
    );

    useEffect(() => {
        navigation.setOptions({
            headerTitle: ({ children }) => (
                <Breadcrumbs>
                    <Breadcrumb href={rootHref} render={breadcrumbLinkRenderer(popToIndex(0))}>
                        {children}
                    </Breadcrumb>
                    {breadcrumbs}
                </Breadcrumbs>
            ),
        });
    }, [breadcrumbs, navigation, popToIndex, rootHref]);
}

/**
 * Renders a breadcrumb's anchor, popping the screens above the one it names.
 *
 * The anchor carries its screen's URL so the browser can open the trail in a
 * new tab or show where a crumb leads, and a plain click pops instead of
 * following it: the screen is still mounted, so returning to it keeps the
 * params and state it already had. Modified clicks are left to the browser.
 *
 * A step already naming the screen on display has nothing to pop to, and a
 * step with no destination is not an anchor at all.
 */
export function breadcrumbLinkRenderer(pop?: () => void): NonNullable<React.ComponentProps<typeof Breadcrumb>['render']> {
    return function renderBreadcrumbLink(props) {
        if (!('href' in props)) {
            return <span {...props} />;
        }

        return (
            <a
                {...props}
                onClick={(event) => {
                    props.onClick?.(event);

                    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
                        return;
                    }

                    event.preventDefault();
                    pop?.();
                }}
            />
        );
    };
}

type NestedStackLayoutProps = NestedStackBreadcrumbsProps & {
    children: React.ReactNode;
};

/**
 * A navigator layout for showing breadcrumbs of a nested stack.
 *
 * Sets the header title of a containing navigator to breadcrumbs of the nested
 * stack state.
 */
export function NestedStackBreadcrumbsLayout({ children, descriptors, navigation, state }: NestedStackLayoutProps) {
    useNestedStackBreadcrumbs({ descriptors, navigation, state });
    return children;
}
